import { createRef, render } from "preact";
import { computed, signal } from "@preact/signals";
import type { Material } from "../appearance.ts";
import { Paws } from "./paws.tsx";
import styles from "./overlays.module.css";

/**
 * Screens laid over everything for a moment: the paw prints while a stage is being made
 * ready, and, on delivery, the kiln rising over the piece or the cat-face iris closing on it.
 */
export const overlays = {
  loading: signal(false),
  kiln: signal(false),
  iris: signal(false),
};

/** How long the kiln takes to rise and to go down again (ms). */
const KILN = { rise: 600, lower: 600 };
/** How long the iris takes to close on the piece and to open again (ms). */
const IRIS = { close: 800, open: 900 };
/** The iris wide open (the face well past the screen's corners), and shut. */
const IRIS_OPEN = "400vmax 400vmax, 100% 100%", IRIS_SHUT = "0px 0px, 100% 100%";
const EASE = "cubic-bezier(0.3, 0.7, 0.4, 1)";
const UP = [{ transform: "translateY(110%)" }, { transform: "translateY(0)" }];

const kilnBody = createRef<HTMLDivElement>(), iris = createRef<HTMLDivElement>();

export async function raiseKiln(): Promise<void> {
  overlays.kiln.value = true;
  await kilnBody.current!.animate(UP, { duration: KILN.rise, easing: EASE, fill: "forwards" })
    .finished;
}

export async function lowerKiln(): Promise<void> {
  await kilnBody.current!.animate([...UP].reverse(), {
    duration: KILN.lower,
    easing: EASE,
    fill: "forwards",
  }).finished;
  overlays.kiln.value = false;
}

/** Closes the iris, its cover in the colour of the piece's material. */
export async function closeIris(cover: Material): Promise<void> {
  iris.current!.dataset.cover = cover;
  overlays.iris.value = true;
  // Most of the shrinking happens off the screen: the face shows for the later part.
  await iris.current!.animate(
    [
      { maskSize: IRIS_OPEN },
      { maskSize: "120vmax 120vmax, 100% 100%", offset: 0.3 },
      { maskSize: IRIS_SHUT },
    ],
    { duration: IRIS.close, easing: "ease-in", fill: "forwards" },
  ).finished;
}

export async function openIris(): Promise<void> {
  await iris.current!.animate(
    [
      { maskSize: IRIS_SHUT },
      { maskSize: "120vmax 120vmax, 100% 100%", offset: 0.7 },
      { maskSize: IRIS_OPEN },
    ],
    { duration: IRIS.open, easing: "ease-out", fill: "forwards" },
  ).finished;
  overlays.iris.value = false;
}

// Bound straight to the elements, so they show the moment the signal changes.
const hidden = {
  loading: computed(() => !overlays.loading.value),
  kiln: computed(() => !overlays.kiln.value),
  iris: computed(() => !overlays.iris.value),
};

export function mountOverlays(root: HTMLElement): void {
  // Kept in the page (hidden) so the animations can start the moment they are shown.
  render(
    <>
      <div class={styles.kiln} hidden={hidden.kiln}>
        <div class={styles.kilnBody} ref={kilnBody}>
          <div class={styles.mouth}>
            <div class={styles.fire} />
          </div>
          <p class={styles.kilnText}>窯で焼いています…</p>
        </div>
      </div>
      <div class={styles.iris} ref={iris} hidden={hidden.iris}>
        <Paws />
        <p class={styles.text}>仕上げています…</p>
      </div>
      <div class={styles.loading} hidden={hidden.loading}>
        <Paws label="読み込み中" />
        <p class={styles.text}>粘土を用意しています…</p>
      </div>
    </>,
    root,
  );
}
